import { CheckCircle2, Lock } from "lucide-react"

import { cn } from "@/lib/utils"

const STEPS = [
  { label: "Added", tab: "overview" },
  { label: "Enriched", tab: "enrichment" },
  { label: "Vendor Understood", tab: "vendor" },
  { label: "Concept Defined", tab: "concept" },
  { label: "Appraised", tab: "appraisal" },
  { label: "Negotiating", tab: "negotiation" },
]

export function DealProgressTracker({ maxUnlockedStage = 0, onStepClick }) {
  const clampedStage = Math.min(Math.max(maxUnlockedStage, 0), STEPS.length - 1)
  const completedCount = Math.min(clampedStage + 1, STEPS.length)
  const progressRatio = clampedStage / (STEPS.length - 1)
  const progressWidth = clampedStage === 0
    ? "0px"
    : `calc(${progressRatio * 100}% - ${32 * progressRatio}px)`

  return (
    <section className="rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">Deal Progress</h2>
        <span className="rounded-md bg-muted px-2 py-1 text-xs font-bold text-foreground">
          {completedCount} / {STEPS.length} Complete
        </span>
      </div>

      <div className="overflow-x-auto pb-1">
        <div className="relative flex min-w-[34rem] items-start justify-between gap-3">
          <div className="absolute left-4 right-4 top-4 h-0.5 bg-muted" />
          <div
            className="absolute left-4 top-4 h-0.5 bg-primary transition-all"
            style={{ width: progressWidth }}
          />
          {STEPS.map((step, index) => (
            <ProgressStep
              key={step.label}
              step={step}
              index={index}
              maxUnlockedStage={clampedStage}
              onStepClick={onStepClick}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function ProgressStep({ step, index, maxUnlockedStage, onStepClick }) {
  const isCompleted = index < maxUnlockedStage
  const isCurrent = index === maxUnlockedStage
  const isLocked = index > maxUnlockedStage
  const isClickable = !isLocked && typeof onStepClick === "function"

  const handleClick = () => {
    if (!isClickable) return

    onStepClick(step.tab)
  }

  return (
    <button
      type="button"
      disabled={!isClickable}
      aria-label={`Open ${step.label}`}
      className="relative z-10 flex w-20 shrink-0 flex-col items-center gap-3"
      title={isLocked ? "Locked" : undefined}
      onClick={handleClick}
    >
      <span
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full border-2 bg-card transition-colors",
          isCompleted && "border-primary bg-primary text-primary-foreground",
          isCurrent && "border-primary text-primary ring-4 ring-primary/20",
          isLocked && "border-muted-foreground/30 bg-muted/50 text-muted-foreground/50",
          isClickable && "cursor-pointer hover:ring-4 hover:ring-primary/20",
        )}
      >
        {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : null}
        {isCurrent ? <span className="text-xs font-bold">{index + 1}</span> : null}
        {isLocked ? <Lock className="h-4 w-4" /> : null}
      </span>
      <span
        className={cn(
          "text-center text-[10px] font-bold leading-tight",
          isCompleted && "text-foreground",
          isCurrent && "text-primary",
          isLocked && "text-muted-foreground/60",
        )}
      >
        {step.label}
      </span>
    </button>
  )
}
